import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { nearbyData } from 'src/assets/database/nearbyData';

import largeNearbyMarker1 from 'src/assets/images/user-mobile/map/nearby/marker/large-marker-1.png';
import largeNearbyMarker2 from 'src/assets/images/user-mobile/map/nearby/marker/large-marker-2.png';
import largeNearbyMarker3 from 'src/assets/images/user-mobile/map/nearby/marker/large-marker-3.png';
import largeNearbyMarker4 from 'src/assets/images/user-mobile/map/nearby/marker/large-marker-4.png';
import largeNearbyMarker5 from 'src/assets/images/user-mobile/map/nearby/marker/large-marker-5.png';
import largeNearbyMarker6 from 'src/assets/images/user-mobile/map/nearby/marker/large-marker-6.png';
import largeNearbyMarker7 from 'src/assets/images/user-mobile/map/nearby/marker/default-marker.png';

import smallNearbyMarker1 from 'src/assets/images/user-mobile/map/nearby/marker/small-marker-1.png';
import smallNearbyMarker2 from 'src/assets/images/user-mobile/map/nearby/marker/small-marker-2.png';
import smallNearbyMarker3 from 'src/assets/images/user-mobile/map/nearby/marker/small-marker-3.png';
import smallNearbyMarker4 from 'src/assets/images/user-mobile/map/nearby/marker/small-marker-4.png';
import smallNearbyMarker5 from 'src/assets/images/user-mobile/map/nearby/marker/small-marker-5.png';
import smallNearbyMarker6 from 'src/assets/images/user-mobile/map/nearby/marker/small-marker-6.png';

import NearbyClickPopup from './NearbyClickPopup';

export default function NearbyComponentMap() {
  const mapRef = useRef<naver.maps.Map>();
  const polylineRef = useRef<naver.maps.Polyline | null>(null);
  const [zoom, setZoom] = useState(13);
  const [markerPopup, setMarkerPopup] = useState(0);
  const markersRef = useRef<naver.maps.Marker[]>([]);

  const INITIAL_COORDINATES = {
    lat: 36.80505057674814,
    lng: 126.77934402867268,
  };

  const markerImages = {
    small: [
      smallNearbyMarker1,
      smallNearbyMarker2,
      smallNearbyMarker3,
      smallNearbyMarker4,
      smallNearbyMarker5,
      smallNearbyMarker6,
      largeNearbyMarker7,
    ],
    large: [
      largeNearbyMarker1,
      largeNearbyMarker2,
      largeNearbyMarker3,
      largeNearbyMarker4,
      largeNearbyMarker5,
      largeNearbyMarker6,
      largeNearbyMarker7,
    ],
  };

  const markerSizes = {
    small: [
      { width: 74, height: 73 },
      { width: 68, height: 109 },
      { width: 59, height: 136 },
      { width: 85, height: 108 },
      { width: 56, height: 104 },
      { width: 112, height: 96 },
      { width: 70, height: 37.522 },
    ],
    large: [
      { width: 129, height: 101 },
      { width: 84, height: 152 },
      { width: 90, height: 195 },
      { width: 132, height: 151 },
      { width: 86, height: 145 },
      { width: 129, height: 129 },
      { width: 70, height: 37.522 },
    ],
  };

  const getMarkerIcon = (id: number, currentZoom: number) => {
    const type = currentZoom < 13 ? 'small' : 'large';
    const image = markerImages[type][id - 1];
    const size = markerSizes[type][id - 1];

    return {
      url: image,
      scaledSize: new naver.maps.Size(size.width, size.height),
      anchor:
        id === 1
          ? new naver.maps.Point(0, 0)
          : new naver.maps.Point(size.width / 2, size.height),
    };
  };

  useEffect(() => {
    if (!mapRef.current) {
      mapRef.current = new naver.maps.Map('map', {
        center: new naver.maps.LatLng(
          INITIAL_COORDINATES.lat,
          INITIAL_COORDINATES.lng,
        ),
        zoom,
        minZoom: 11,
        maxZoom: 16,
      });

      naver.maps.Event.addListener(
        mapRef.current,
        'zoom_changed',
        (_zoom: number) => {
          setZoom(_zoom);
          markersRef.current.forEach((marker, index) => {
            const item = nearbyData[index];
            marker.setIcon(getMarkerIcon(item.id, _zoom));
          });
        },
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      naver.maps.Event.addListener(mapRef.current, 'click', (e: any) => {
        const target = e?.domEvent?.target as HTMLElement | undefined;
        if (target?.closest('.popup-card')) return;
        setMarkerPopup(0);
      });
    }

    if (mapRef.current && markersRef.current.length === 0) {
      const newMarkers = nearbyData.map(item => {
        const icon = getMarkerIcon(item.id, zoom);

        const marker = new naver.maps.Marker({
          position: new naver.maps.LatLng(item.latitude, item.longitude),
          map: mapRef.current,
          icon,
        });

        if (item.id !== 7) {
          naver.maps.Event.addListener(marker, 'click', () => {
            setMarkerPopup(item.id);
            let adjustedLat = item.latitude - 0.009;
            if (item.id === 1) adjustedLat -= 0.008;
            const adjustedLng = item.longitude;
            const targetLatLng = new naver.maps.LatLng(
              adjustedLat,
              adjustedLng,
            );
            mapRef.current?.morph(targetLatLng, 14, { duration: 700 });
          });
        }

        return marker;
      });

      markersRef.current = newMarkers;
      const polylinePath = [
        new naver.maps.LatLng(36.82005064292205, 126.78486579700021),
        new naver.maps.LatLng(36.81849876294469, 126.78377737254905),
        new naver.maps.LatLng(36.81815180749499, 126.78376153676311),
        new naver.maps.LatLng(36.81785200031005, 126.78242305029941),
        new naver.maps.LatLng(36.817566230720374, 126.78137593554285),
        new naver.maps.LatLng(36.81663426410011, 126.77930517950728),
        new naver.maps.LatLng(36.81642645871379, 126.77900877542544),
        new naver.maps.LatLng(36.816151038532084, 126.7786957562808),
        new naver.maps.LatLng(36.81522649901304, 126.77821089598906),
        new naver.maps.LatLng(36.81477574852036, 126.7781057267391),
        new naver.maps.LatLng(36.8140819194786, 126.77811893654703),
        new naver.maps.LatLng(36.81337863999672, 126.77789682367097),
        new naver.maps.LatLng(36.81297265102982, 126.7776346304033),
        new naver.maps.LatLng(36.81222753935066, 126.77672902166603),
        new naver.maps.LatLng(36.81173493054074, 126.77592915967787),
        new naver.maps.LatLng(36.81051861930351, 126.77602796131613),
        new naver.maps.LatLng(36.81040367448387, 126.77480676652713),
        new naver.maps.LatLng(36.809750628437875, 126.77494315829425),
        new naver.maps.LatLng(36.8012229608577, 126.76975762266102),
        new naver.maps.LatLng(36.80131038457603, 126.76953325361721),
        new naver.maps.LatLng(36.801119836899154, 126.76885590152507),
        new naver.maps.LatLng(36.80053754558356, 126.76946273511719),
        new naver.maps.LatLng(36.79716983718192, 126.76840277296488),
        new naver.maps.LatLng(36.796881005136896, 126.76931682735274),
        new naver.maps.LatLng(36.79036497883035, 126.78316182158984),
        new naver.maps.LatLng(36.7896413598489, 126.78413858010401),
        new naver.maps.LatLng(36.78999822148798, 126.784647348397),
        new naver.maps.LatLng(36.79046698011188, 126.78474687059095),
        new naver.maps.LatLng(36.79085018403733, 126.78487464282894),
        new naver.maps.LatLng(36.79307083512154, 126.7833082532111),
        new naver.maps.LatLng(36.79469119243488, 126.7836426227182),
        new naver.maps.LatLng(36.79442421713264, 126.78425121044131),
        new naver.maps.LatLng(36.79424071269295, 126.78492678657312),
        new naver.maps.LatLng(36.793898180463366, 126.78486612051219),
        new naver.maps.LatLng(36.79338991002262, 126.78534091991399),
        new naver.maps.LatLng(36.793014399644235, 126.78573131223206),
        new naver.maps.LatLng(36.79330111134496, 126.78606944093714),
        new naver.maps.LatLng(36.79265118792155, 126.78669867819481),
        new naver.maps.LatLng(36.79114345682417, 126.7863527363605),
        new naver.maps.LatLng(36.790737021392054, 126.78583288674028),
        new naver.maps.LatLng(36.79044063054284, 126.78512507213074),
        new naver.maps.LatLng(36.79047596077745, 126.78473003977909),
        new naver.maps.LatLng(36.790007212283044, 126.78463611951017),
        new naver.maps.LatLng(36.78962117131915, 126.78418625232739),
        new naver.maps.LatLng(36.78865887572496, 126.78275209872781),
        new naver.maps.LatLng(36.78855418287166, 126.7821698116543),
        new naver.maps.LatLng(36.78843005477515, 126.78204692459224),
        new naver.maps.LatLng(36.78800621054847, 126.78187167098477),
        new naver.maps.LatLng(36.78718844666194, 126.7806444253138),
        new naver.maps.LatLng(36.787066275078054, 126.78036189034795),
        new naver.maps.LatLng(36.7867209956995, 126.78003518088106),
        new naver.maps.LatLng(36.786366206094115, 126.77943962492711),
        new naver.maps.LatLng(36.784536645971045, 126.77686259363594),
        new naver.maps.LatLng(36.78465991224999, 126.77532743292011),
        new naver.maps.LatLng(36.78456885881397, 126.77482916831796),
        new naver.maps.LatLng(36.785028116360415, 126.77466538118614),
        new naver.maps.LatLng(36.78507935139582, 126.77435994929661),
        new naver.maps.LatLng(36.78486691189806, 126.77282016326696),
        new naver.maps.LatLng(36.78467056638655, 126.77145958506554),
        new naver.maps.LatLng(36.7847953526366, 126.77074782638383),
        new naver.maps.LatLng(36.784794518068466, 126.76915140674893),
        new naver.maps.LatLng(36.784638459845, 126.76538208589562),
        new naver.maps.LatLng(36.78449173467629, 126.76409419772685),
        new naver.maps.LatLng(36.783922010868274, 126.76307648613692),
        new naver.maps.LatLng(36.784992474872034, 126.76102024300671),
        new naver.maps.LatLng(36.78546700003846, 126.76173856598915),
        new naver.maps.LatLng(36.78617481772914, 126.76196043784064),
        new naver.maps.LatLng(36.786355016727754, 126.76194867727656),
        new naver.maps.LatLng(36.78629854534479, 126.76075011178),
        new naver.maps.LatLng(36.786378460550786, 126.76016169582606),
        new naver.maps.LatLng(36.78671103362051, 126.75974613951244),
        new naver.maps.LatLng(36.78611966457938, 126.75918222648829),
        new naver.maps.LatLng(36.78579844302572, 126.75852784995382),
        new naver.maps.LatLng(36.784992372793994, 126.7587376343668),
        new naver.maps.LatLng(36.78171286440484, 126.75900557204007),
        new naver.maps.LatLng(36.780510860504684, 126.75949103584993),
        new naver.maps.LatLng(36.77966971548026, 126.76018260015292),
        new naver.maps.LatLng(36.778622435912844, 126.76143489272643),
        new naver.maps.LatLng(36.777875256771026, 126.76180687270855),
        new naver.maps.LatLng(36.77742571078822, 126.76231234862014),
        new naver.maps.LatLng(36.77712056324942, 126.7629293921739),
        new naver.maps.LatLng(36.776822683727744, 126.76267266807572),
        new naver.maps.LatLng(36.7747909516192, 126.76281334923198),
        new naver.maps.LatLng(36.773871601084686, 126.76383830656492),
        new naver.maps.LatLng(36.77383580680247, 126.7639644317259),
        new naver.maps.LatLng(36.77182400028993, 126.76506830418346),
        new naver.maps.LatLng(36.77126887695774, 126.7645939557111),
        new naver.maps.LatLng(36.76957775355493, 126.76382067373048),
        new naver.maps.LatLng(36.7692256198309, 126.76346893183609),
        new naver.maps.LatLng(36.76895874780765, 126.76521705400243),
        new naver.maps.LatLng(36.769198270768854, 126.7655859465534),
        new naver.maps.LatLng(36.764018700389165, 126.77116248149012),
        new naver.maps.LatLng(36.76356705569948, 126.77059822972763),
        new naver.maps.LatLng(36.76299524942566, 126.77080712890377),
        new naver.maps.LatLng(36.76291815849756, 126.77054976336494),
        new naver.maps.LatLng(36.76271098930406, 126.77059517950873),
        new naver.maps.LatLng(36.76268482964564, 126.77104884712489),
      ];

      polylineRef.current = new naver.maps.Polyline({
        map: mapRef.current,
        path: polylinePath,
        strokeColor: '#3877D1',
        strokeOpacity: 1,
        strokeWeight: 6,
        strokeStyle: 'solid',
      });
    }
  }, [zoom]);

  useEffect(() => {
    // 화면 높이 설정
    const setHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setHeight();
    window.addEventListener('resize', setHeight);
    return () => window.removeEventListener('resize', setHeight);
  }, []);

  return (
    <MapContainer id="map">
      {markerPopup !== 0 && (
        <NearbyClickPopup id={markerPopup} onClose={() => setMarkerPopup(0)} />
      )}
    </MapContainer>
  );
}

const MapContainer = styled.div`
  height: calc(var(--vh, 1vh) * 100);
`;
